/* eslint-disable jira/filename-case, jira/i18n/id-named-by-package, jira/naming/disallow-filenames */
import { ENTITY_LIMIT_TYPE as COLLECTION_ENTITY_LIMIT_TYPE } from '@jira/polaris__domain-collection/src/entityLimitType.constants.tsx';
import type { EntityLimitType as CollectionEntityLimitType } from '@jira/polaris__domain-collection/src/EntityLimitType.types.tsx';
import { ENTITY_LIMIT_TYPE as PROJECT_ENTITY_LIMIT_TYPE } from '@jira/polaris__domain-project/src/entityLimitType.constants.tsx';
import type { EntityLimitType as ProjectEntityLimitType } from '@jira/polaris__domain-project/src/ProjectMetadata.types.tsx';
import { defineMessage } from '@atlassian/jira-intl';
import type { MessageDescriptor } from '@atlassian/jira-intl/src/index.tsx';

const commentsPerViewMessage = defineMessage({
	id: 'polaris-component-limits.common.comments-per-view',
	defaultMessage:
		'This view has reached its maximum of {limit, plural, one {# comment} other {# comments}}.',
	description: 'Entity limit warning description for comments per view',
});

const fieldsPerViewMessage = defineMessage({
	id: 'polaris-component-limits.common.fields-per-view',
	defaultMessage:
		'This view has reached its maximum of {limit, plural, one {# field} other {# fields}}.',
	description: 'Entity limit warning description for fields per view',
});

const fieldsPerProjectMessage = defineMessage({
	id: 'polaris-component-limits.common.fields-per-project',
	defaultMessage:
		'This project has reached its maximum of {limit, plural, one {# field} other {# fields}}.',
	description: 'Entity limit warning description for fields per project',
});

const ideasSearchMaxResultsMessage = defineMessage({
	id: 'polaris-component-limits.common.ideas-search-max-results',
	defaultMessage:
		'This search has reached its maximum of {limit, plural, one {# result} other {# results}}.',
	description: 'Entity limit warning description for ideas search max results',
});

const insightsPerIdeaMessage = defineMessage({
	id: 'polaris-component-limits.common.insights-per-idea',
	defaultMessage:
		'This idea has reached its maximum of {limit, plural, one {# insight} other {# insights}}.',
	description: 'Entity limit warning description for insights per idea',
});

const insightsPerProjectMessage = defineMessage({
	id: 'polaris-component-limits.common.insights-per-project',
	defaultMessage:
		'This project has reached its maximum of {limit, plural, one {# insight} other {# insights}}.',
	description: 'Entity limit warning description for insights per project',
});

const labelsPerSnippetMessage = defineMessage({
	id: 'polaris-component-limits.common.labels-per-snippet',
	defaultMessage:
		'This snippet has reached its maximum of {limit, plural, one {# label} other {# labels}}.',
	description: 'Entity limit warning description for labels per snippet',
});

const optionsPerFieldMessage = defineMessage({
	id: 'polaris-component-limits.common.options-per-field',
	defaultMessage:
		'This field has reached its maximum of {limit, plural, one {# option} other {# options}}.',
	description: 'Entity limit warning description for options per field',
});

const playContributionsPerPlayMessage = defineMessage({
	id: 'polaris-component-limits.common.play-contributions-per-play',
	defaultMessage:
		'This vote has reached its maximum of {limit, plural, one {# vote contribution} other {# vote contributions}}.',
	description: 'Entity limit warning description for play contributions per play',
});

const playContributionsPerProjectMessage = defineMessage({
	id: 'polaris-component-limits.common.play-contributions-per-project',
	defaultMessage:
		'This project has reached its maximum of {limit, plural, one {# vote contribution} other {# vote contributions}}.',
	description: 'Entity limit warning description for play contributions per project',
});

const playsPerProjectMessage = defineMessage({
	id: 'polaris-component-limits.common.plays-per-project',
	defaultMessage:
		"You've reached a {limit, plural, one {# vote field} other {# vote fields}} limit per project.",
	description: 'Entity limit warning description for plays per project',
});

const snippetsPerInsightMessage = defineMessage({
	id: 'polaris-component-limits.common.snippets-per-insight',
	defaultMessage:
		'This insight has reached its maximum of {limit, plural, one {# snippet} other {# snippets}}.',
	description: 'Entity limit warning description for snippets per insight',
});

const viewsPerProjectMessage = defineMessage({
	id: 'polaris-component-limits.common.views-per-project',
	defaultMessage:
		'This project has reached its maximum of {limit, plural, one {# view} other {# views}}.',
	description: 'Entity limit warning description for views per project',
});

const viewsPerCollectionMessage = defineMessage({
	id: 'polaris-component-limits.common.views-per-collection',
	defaultMessage:
		'This roadmap has reached its maximum of {limit, plural, one {# view} other {# views}}.',
	description: 'Entity limit warning description for views per roadmap',
});

const viewsetsPerProjectMessage = defineMessage({
	id: 'polaris-component-limits.common.viewsets-per-project',
	defaultMessage:
		'This project has reached its maximum of {limit, plural, one {# section} other {# sections}}.',
	description: 'Entity limit warning description for viewsets per project',
});

const viewsetsPerCollectionMessage = defineMessage({
	id: 'polaris-component-limits.common.viewsets-per-collection',
	defaultMessage:
		'This roadmap has reached its maximum of {limit, plural, one {# section} other {# sections}}.',
	description: 'Entity limit warning description for viewsets per roadmap',
});

const collectionsPerSiteMessage = defineMessage({
	id: 'polaris-component-limits.common.collections-per-site',
	defaultMessage:
		'This site has reached its maximum of {limit, plural, one {# roadmap} other {# roadmaps}}.',
	description: 'Entity limit warning description for roadmaps per site',
});

const messages = {
	[PROJECT_ENTITY_LIMIT_TYPE.COMMENTS_PER_VIEW]: commentsPerViewMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.FIELDS_PER_VIEW]: fieldsPerViewMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.FIELDS_PER_PROJECT]: fieldsPerProjectMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.IDEAS_SEARCH_MAX_RESULTS]: ideasSearchMaxResultsMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.INSIGHTS_PER_IDEA]: insightsPerIdeaMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.INSIGHTS_PER_PROJECT]: insightsPerProjectMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.LABELS_PER_SNIPPET]: labelsPerSnippetMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.OPTIONS_PER_FIELD]: optionsPerFieldMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.PLAY_CONTRIBUTIONS_PER_PLAY]: playContributionsPerPlayMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.PLAY_CONTRIBUTIONS_PER_PROJECT]: playContributionsPerProjectMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.PLAYS_PER_PROJECT]: playsPerProjectMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.SNIPPETS_PER_INSIGHT]: snippetsPerInsightMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.VIEWS_PER_PROJECT]: viewsPerProjectMessage,
	[COLLECTION_ENTITY_LIMIT_TYPE.VIEWS_PER_COLLECTION]: viewsPerCollectionMessage,
	[PROJECT_ENTITY_LIMIT_TYPE.VIEWSETS_PER_PROJECT]: viewsetsPerProjectMessage,
	[COLLECTION_ENTITY_LIMIT_TYPE.VIEWSETS_PER_COLLECTION]: viewsetsPerCollectionMessage,
	[COLLECTION_ENTITY_LIMIT_TYPE.COLLECTIONS_PER_SITE]: collectionsPerSiteMessage,
};

export const getLimitMessageDescriptor = (
	limitType: ProjectEntityLimitType | CollectionEntityLimitType,
): MessageDescriptor | null => messages[limitType] || null;
